import { Button, Dialog } from "@jugl-web/ui-kit";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import React, { useEffect, useMemo, useState } from "react";
import { useClientSupportPageContext } from "../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useWhatsappAccount } from "../../providers/WhatsappAccountProvider/useWhatsappAccount";
import { formatWhatsappPhoneNumber } from "../ClientSupportChat/utils/formatWhatsappPhoneNumber";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

export type ManageContactAlertVariant = "block" | "unblock";

export interface ManageContactAlertProps {
  variant?: ManageContactAlertVariant;
  contactName: string;
  isOpen: boolean;
  onRequestClose: () => void;
  onConfirm: (variant: ManageContactAlertVariant) => void;
}

export const ManageContactAlert: React.FC<ManageContactAlertProps> = ({
  variant,
  contactName,
  isOpen,
  onRequestClose,
  onConfirm,
}) => {
  const { t } = useTranslations();
  const { isCx } = useAppVariant();
  const { selectedConversation } = useClientSupportPageContext();
  const { whatsappAgent } = useWhatsappAccount();
  const [displayVariant, setDisplayVariant] = useState<
    ManageContactAlertVariant | undefined
  >();

  useEffect(() => {
    if (isOpen && variant) {
      setDisplayVariant(variant);
    }
  }, [isOpen, variant]);

  const title = useMemo(() => {
    if (displayVariant === "block") {
      return isCx
        ? t({
            id: "client-support-page.block-customer",
            defaultMessage: "⚠️ Block Customer",
          })
        : t({
            id: "client-support-page.block-user",
            defaultMessage: "⚠️ Block User",
          });
    }

    return isCx
      ? t({
          id: "client-support-page.unblock-customer-emoji",
          defaultMessage: "🔁 Unblock Customer",
        })
      : t({
          id: "client-support-page.unblock-user-emoji",
          defaultMessage: "🔁 Unblock User",
        });
  }, [displayVariant, isCx, t]);

  const phoneNumber = useMemo(
    () =>
      selectedConversation
        ? formatWhatsappPhoneNumber(
            selectedConversation.conv_id,
            whatsappAgent?.settings.hide_num || false
          )
        : "",
    [selectedConversation, whatsappAgent?.settings.hide_num]
  );

  const content = useMemo(() => {
    if (displayVariant === "block") {
      return t(
        {
          id: "client-support-page.block-customer-confirmation",
          defaultMessage:
            "Are you sure you want to block {name}? User will no longer be able to contact your Business WhatsApp account using {phoneNumber} phone number",
        },
        {
          name: (
            <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
              {contactName}
            </span>
          ),
          phoneNumber: (
            <span className="font-medium text-primary">{phoneNumber}</span>
          ),
        }
      );
    }

    return t(
      {
        id: "client-support-page.unblock-customer-confirmation",
        defaultMessage:
          "Are you sure you want to unblock {name}? User will be able to contact your Business WhatsApp account using {phoneNumber} phone number",
      },
      {
        name: (
          <span className="bg-linear-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
            {contactName}
          </span>
        ),
        phoneNumber: (
          <span className="font-medium text-primary">{phoneNumber}</span>
        ),
      }
    );
  }, [contactName, displayVariant, phoneNumber, t]);

  const confirmLabel =
    displayVariant === "block"
      ? t({
          id: "client-support-page.block",
          defaultMessage: "Block",
        })
      : t({
          id: "client-support-page.unblock",
          defaultMessage: "Unblock",
        });

  const handleConfirm = () => {
    if (!displayVariant) {
      return;
    }

    onConfirm(displayVariant);
    onRequestClose();
  };

  return (
    <Dialog
      isOpen={isOpen && !!displayVariant}
      onClose={onRequestClose}
      className="flex w-full max-w-[480px] flex-col overflow-hidden rounded-2xl p-10"
    >
      <span className="font-secondary text-[22px] font-bold text-dark">
        {title}
      </span>
      <span className="mt-2.5 font-secondary text-sm leading-[21px] text-dark-700">
        {content}
      </span>
      <div className="mt-14 flex items-center gap-4">
        <Button
          onClick={onRequestClose}
          variant="tertiary"
          className="w-[140px]"
        >
          {t({
            id: "common.cancel",
            defaultMessage: "Cancel",
          })}
        </Button>
        <Button
          onClick={handleConfirm}
          iconEnd={
            displayVariant === "unblock" ? <ArrowRightIcon /> : undefined
          }
          className="flex-1"
          variant={displayVariant === "block" ? "danger" : "primary"}
        >
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
};
